<template>
  <div class="FloorPlannerIframe desk:layout-container">
    <!--    <DebugOutput>{{ materialoLink }}</DebugOutput>-->
    <iframe
      class="w-full h-[calc(100dvh-128px)] desk:h-[calc(100dvh-137px)]"
      :src="materialoLink"
      :title="$t('floorPlanner.iframeTitle')"
    />
  </div>
</template>

<script setup lang="ts">
import { useGlobalContentStore } from '~/store/globalContent';
import { useCartStore } from '~/store/cart';
import { useUiStore } from '~/store/ui';
import type { ProductRaw } from '#constants/types/algolia';
import { sendDataLayer, createDataLayerItem } from '~/composables/useTracking';
const globalContent = useGlobalContentStore();
const cartStore = useCartStore();
const uiStore = useUiStore();
const { getProductByPartNo, getExactVariantByPartNo, convertRawToProducts } = useAlgoliaFetch();

const { $t } = useNuxtApp();

const handleOpenQuickbuy = async(partNo: string) => {
  const result = await getProductByPartNo(partNo);
  if (!result?.hits.length) return;

  const [item] = convertRawToProducts(result.hits, [partNo]);
  if (!item?.product) return;

  cartStore.setCurrentlyBuying(item.product);
  uiStore.setShowQuickBuy(true);
  uiStore.setShowMiniCart(false);
};

const handleOrderSample = async(partNos: string[]) => {
  if (!partNos.length) return;

  const res = await getExactVariantByPartNo(partNos);
  if (!res?.hits.length) return;

  let samplePartNo: string | undefined;
  if (globalContent.getIsB2c) {
    const prefix = globalContent.domainMap?.countryCode === 'us' ? 'US01' : 'SA01';
    samplePartNo = res.hits.find((h) => h.partNo?.toUpperCase().startsWith(prefix))?.partNo;
  } else {
    samplePartNo = res.hits[0]?.partNo;
  }

  if (!samplePartNo) return;
  await cartStore.updateCart(samplePartNo);
};

let selectorSource: MessageEventSource | null = null;

const pushCartToSelector = () => {
  const partNos = cartStore.cart.items.map((i) => i.partNo);
  selectorSource?.postMessage({ action: 'kahrs:cartData', partNos }, '*');
};

watch(() => cartStore.cart.items, pushCartToSelector, { deep: true });

const onMessage = async(event: MessageEvent) => {
  if (!event.data?.action) return;

  if (event.data.action === 'kahrs:requestCart') {
    selectorSource = event.source;
    pushCartToSelector();
    (selectorSource as Window | null)?.postMessage({ action: 'kahrs:artNr', artNr: props.art_nr }, '*');
  } else if (event.data.action === 'kahrs:openQuickbuy') {
    const { partNo } = event.data;
    if (partNo) await handleOpenQuickbuy(partNo);
  } else if (event.data.action === 'kahrs:orderSample') {
    const { partNos } = event.data;
    if (Array.isArray(partNos)) await handleOrderSample(partNos);
  } else if (event.data.action === 'kahrs:trackFloorSelected') {
    const product = event.data.product as ProductRaw;
    sendDataLayer('floor_planner_floor_selected', [createDataLayerItem(product)]);
  } else if (event.data.action === 'kahrs:trackGoToProduct') {
    const product = event.data.product as ProductRaw;
    sendDataLayer('floor_planner_go_to_product', [createDataLayerItem(product)]);
  } else if (event.data.action === 'kahrs:trackOrderSample') {
    const product = event.data.product as ProductRaw;
    sendDataLayer('floor_planner_add_sample_to_cart', [createDataLayerItem(product)]);
  }
};

onMounted(() => window.addEventListener('message', onMessage));
onUnmounted(() => window.removeEventListener('message', onMessage));

const props = defineProps<{
  art_nr: string,
  art_name: string,
  design?: string | null,
  surface?: string | null,
  construction?: string | null,
}>();

const materialoLink = computed(()=> {
  const query = [] as string[];

  query.push('start_text='+$t('floorPlanner.headline1'));
  query.push('start_text2='+$t('floorPlanner.headline2'));
  query.push(`lng_str=${globalContent.getFloorPlannerLocale}`);
  // Not actually language but tld:
  if (globalContent.getCountryCode === 'us' || globalContent.getCountryCode === 'gb' || globalContent.getCountryCode === 'global' || globalContent.getCountryCode === 'fi') {
    query.push('language=en');
  } else if (globalContent.getCountryCode === 'at') {
    query.push('language=de');
  } else {
    query.push(`language=${globalContent.domainMap?.storyblokBasePath}`);
  }
  query.push('type=Floors');

  query.push(`art_nr=${props.art_nr}`);
  query.push(`art_name=${props.art_name}`);

  if (props.design) {
    query.push(`design=${props.design}`);
  }
  if (props.surface) {
    query.push(`surface=${props.surface}`);
  }
  if (props.construction) {
    query.push(`construction=${props.construction}`);
  }

  // Temporary link using cloudflare tunnel to expose localhost
  const selectorLink = globalContent.getEnvironment === 'local' ? 'dev.iveo.store/uk/floorplannerSelector' : `${globalContent.domainMap?.domain}/${globalContent.domainMap?.storyblokBasePath}/floorplannerSelector`;

  query.push(`iframe_url=${selectorLink}`);

  return `https://kahrs.materialo.com/?${encodeURI(query.join('&'))}`;
});

</script>
